/**
 * MusicLyricsImportSheet - 歌詞サイトの URL から歌詞を取り込むボトムシート
 *
 * 曲メニューや歌詞タブから開くため、実体は MiniPlayer 側で1つだけマウントし、
 * 開閉と対象の曲は useLyricsImport で受け取る。
 * URL を送るとサーバー側が AI でページから歌詞本文を抽出するので、
 * 結果をテキストエリアで確認・修正してから保存する。
 */

import { Sparkles } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { extractEpisodeLyrics, saveEpisodeLyrics } from '@/api/api'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { useSubmitLock } from '@/hooks/useSubmitLock'

import { useLyricsImport } from './use-lyrics-import'

/** エラーメッセージはサーバーが理由まで返すため、あればそれを優先して表示する */
const toErrorMessage = (error: unknown, fallback: string): string => {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof detail === 'string' ? detail : fallback
}

export const MusicLyricsImportSheet = () => {
  const target = useLyricsImport((s) => s.target)
  const closeLyricsImport = useLyricsImport((s) => s.closeLyricsImport)
  const notifyLyricsSaved = useLyricsImport((s) => s.notifyLyricsSaved)

  const [url, setUrl] = useState('')
  const [text, setText] = useState('')
  const [language, setLanguage] = useState<string | null>(null)
  const [songwriters, setSongwriters] = useState<string[]>([])
  const { isSubmitting: isExtracting, run: runExtract } = useSubmitLock()
  const { isSubmitting: isSaving, run: runSave } = useSubmitLock()

  const isOpen = target !== null

  useEffect(() => {
    if (!isOpen) return
    setUrl('')
    setText('')
    setLanguage(null)
    setSongwriters([])
  }, [isOpen])

  const handleExtract = () => {
    const trimmed = url.trim()
    if (!target || !trimmed) return

    void runExtract(async () => {
      try {
        const res = await extractEpisodeLyrics(target.episodeId, trimmed)
        setText(res.text)
        setLanguage(res.language)
        setSongwriters(res.songwriters)
        toast.success('歌詞を取得しました。内容を確認して保存してください')
      } catch (error) {
        toast.error(toErrorMessage(error, '歌詞の取得に失敗しました'))
      }
    })
  }

  const handleSave = () => {
    if (!target || !text.trim()) return

    void runSave(async () => {
      try {
        await saveEpisodeLyrics(target.episodeId, { text, language, songwriters })
        toast.success('歌詞を保存し、タイムスタンプの同期を予約しました')
        notifyLyricsSaved()
        closeLyricsImport()
      } catch (error) {
        toast.error(toErrorMessage(error, '歌詞の保存に失敗しました'))
      }
    })
  }

  return (
    <BottomSheet
      title="歌詞を追加"
      open={isOpen}
      onOpenChange={(open) => !open && closeLyricsImport()}
      snapPoints={[0.92]}
      footer={
        <div className="px-4 pt-2 pb-6">
          <Button className="w-full" disabled={!text.trim() || isSaving} onClick={handleSave}>
            {isSaving ? '保存中...' : '保存'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 px-4 pb-4">
        {target && <div className="truncate text-sm text-muted-foreground">{target.title}</div>}

        <div className="flex gap-2">
          <Input
            value={url}
            type="url"
            inputMode="url"
            maxLength={2048}
            placeholder="歌詞ページのURL"
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleExtract()}
          />
          <Button variant="secondary" disabled={!url.trim() || isExtracting} onClick={handleExtract}>
            {isExtracting ? <Spinner size={18} /> : <Sparkles size={18} />}
            取得
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          歌詞サイトのURLを貼り付けると、AIがページから歌詞本文だけを取り出します。
        </p>

        <Textarea
          value={text}
          rows={16}
          maxLength={20000}
          placeholder="取得した歌詞がここに表示されます。手直ししてから保存できます"
          onChange={(e) => setText(e.target.value)}
        />

        {songwriters.length > 0 && (
          <div className="text-xs text-muted-foreground">作詞・作曲: {songwriters.join(' / ')}</div>
        )}

        <p className="text-xs text-muted-foreground">
          保存すると、再生に合わせて歌詞を光らせるためのタイムスタンプ同期を予約します。
        </p>
      </div>
    </BottomSheet>
  )
}
